'use client'

import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { AlertTriangle, Home, RotateCcw } from 'lucide-react'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Application error:', error)
  }, [error])

  return (
    <div className="min-h-screen bg-gradient-to-br from-encore-navy-950 via-encore-navy-900 to-red-900/30 flex items-center justify-center px-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-72 h-72 bg-red-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-72 h-72 bg-encore-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative text-center max-w-lg mx-auto">
        {/* Error Icon */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl scale-150" />
            <div className="relative p-6 rounded-full bg-gradient-to-br from-red-500/20 to-red-600/20 border border-red-500/30">
              <AlertTriangle className="h-16 w-16 text-red-400" />
            </div>
          </div>
        </div>

        {/* Message */}
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
          Something went wrong
        </h1>
        <p className="text-encore-gray-300 mb-4 text-lg">
          We encountered an unexpected error. Don&apos;t worry, our team has been notified.
        </p>

        {/* Error digest for support */}
        {error.digest && (
          <p className="text-encore-gray-500 text-sm mb-8">
            Error ID: <code className="bg-encore-gray-800 px-2 py-1 rounded">{error.digest}</code>
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            onClick={reset}
            size="lg"
            className="bg-gradient-to-r from-encore-purple-600 to-encore-cyan-600 hover:from-encore-purple-500 hover:to-encore-cyan-500 border-0"
          >
            <RotateCcw className="mr-2 h-5 w-5" />
            Try Again
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="border-encore-gray-600 text-white hover:bg-white/10"
            onClick={() => window.location.href = '/'}
          >
            <Home className="mr-2 h-5 w-5" />
            Go to Dashboard
          </Button>
        </div>

        {/* Help text */}
        <p className="mt-8 text-encore-gray-500 text-sm">
          If the problem persists, please contact{' '}
          <a href="mailto:support@encore-ai.com" className="text-encore-cyan-400 hover:underline">
            support@encore-ai.com
          </a>
        </p>

        {/* Decorative element */}
        <div className="mt-12 flex items-center justify-center gap-2 text-encore-gray-500">
          <span className="h-px w-12 bg-gradient-to-r from-transparent to-encore-gray-600" />
          <span className="text-sm">Encore-AI Portal</span>
          <span className="h-px w-12 bg-gradient-to-l from-transparent to-encore-gray-600" />
        </div>
      </div>
    </div>
  )
}

